// Case list — the inbox. Each row reads like an email preview: unread dot,
// title (bold when the case has activity this browser hasn't seen), one meta
// line, and a snippet of the LAST message with its author ("Chauncey White:
// We'll consolidate the buttons…") — so "what did they ask / what have I
// said" is answered at a glance, without opening anything.
//
// The list stays grouped into collapsible workflow stages ("Needs attention"
// open by default; In progress / Fixed / Closed collapsed) so worked cases
// drop out of the top of the screen. Within "Needs attention", cases whose
// ball is in the dev court sort first. Stats live on the dashboard — the old
// summary strip is gone on purpose.
//
// "Filed by me" is server-side (filed_by_me=true → opened_by OR incident
// reporter). Unread state comes from lib/seen.ts (localStorage last-seen).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { CaseView } from '../components/CaseView';
import { useCanDock } from '../lib/useMediaQuery';
import {
  QC_CASE_STATUSES,
  QC_ENVIRONMENTS,
  type QcCase,
  type QcCasePriority,
  type QcCaseStatus,
  type QcCaseType,
  type QcEnvironment,
  type QcModule,
} from '@itms-qc/shared';
import { api } from '../lib/api';
import { useApiToken } from '../auth/useApiToken';
import {
  conversationCourt,
  CourtChip,
  ModuleBadge,
  PriorityPill,
  StatusPill,
  STATUS_LABELS,
  TypeIcon,
} from '../components/badges';
import { NewCaseModal } from '../components/NewCaseModal';
import { absoluteTime, actorLabel, personLabel, relativeTime } from '../lib/format';
import { isUnread } from '../lib/seen';
import { useMe } from '../lib/me';
import { inputStyle, primaryButtonStyle, selectStyle } from '../components/form';

const MONO = 'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)';
const FILTERS_KEY = 'itms-qc:case-filters';

interface Filters {
  search: string;
  moduleId: number | '';
  // Bugs and feature requests are SEPARATE LANES, never one list (Chauncey,
  // 2026-07-01: mixing them invites acting on a feature request as if it
  // were a bug fix). There is deliberately no "All cases" view.
  type: QcCaseType;
  priority: QcCasePriority | '';
  filedByMe: boolean;
}

const DEFAULT_FILTERS: Filters = {
  search: '',
  moduleId: '',
  type: 'bug',
  priority: '',
  filedByMe: false,
};

function loadFilters(): Filters {
  let base = DEFAULT_FILTERS;
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    if (raw) base = { ...DEFAULT_FILTERS, ...(JSON.parse(raw) as Partial<Filters>) };
  } catch {
    /* fall through to defaults */
  }
  // Pre-lane persisted filters may hold '' ("All") — coerce into the bug lane.
  if (base.type !== 'bug' && base.type !== 'feature_request') {
    base = { ...base, type: 'bug' };
  }
  // Honor a ?type= deep link (e.g. from the dashboard).
  try {
    const t = new URLSearchParams(window.location.search).get('type');
    if (t === 'bug' || t === 'feature_request') return { ...base, type: t };
  } catch {
    /* ignore */
  }
  return base;
}

// Chip-styled select wrapper for the filter bar (mockup chips, but live).
const chipSelectStyle: React.CSSProperties = {
  ...selectStyle,
  width: 'auto',
  fontSize: 12,
  padding: '5px 8px',
  borderRadius: 6,
};

// The list is grouped into workflow stages so cases we're actively working on
// drop out of the top of the screen. "Needs attention" stays open; the rest
// (cases already being handled or done) collapse by default, keeping the
// untouched backlog short. Open/closed state persists per browser.
type SectionKey = 'needs' | 'accepted' | 'in_progress' | 'fixed' | 'closed';

const SECTIONS: {
  key: SectionKey;
  label: string;
  statuses: QcCaseStatus[];
  defaultOpen: boolean;
}[] = [
  { key: 'needs', label: 'Needs attention', statuses: ['new', 'triaged'], defaultOpen: true },
  // Feature requests that got the explicit ✓ Accept decision — the roadmap
  // backlog, waiting for a build slot. (Empty in the bug lane.)
  { key: 'accepted', label: 'Accepted — backlog', statuses: ['accepted'], defaultOpen: false },
  { key: 'in_progress', label: 'In progress', statuses: ['in_progress'], defaultOpen: false },
  { key: 'fixed', label: 'Fixed — awaiting verify', statuses: ['fixed'], defaultOpen: false },
  {
    key: 'closed',
    label: 'Closed',
    statuses: ['closed', 'wont_do', 'duplicate'],
    defaultOpen: false,
  },
];

const SECTIONS_KEY = 'itms-qc:case-sections';

function loadSections(): Record<SectionKey, boolean> {
  const base = Object.fromEntries(SECTIONS.map((s) => [s.key, s.defaultOpen])) as Record<
    SectionKey,
    boolean
  >;
  try {
    const raw = localStorage.getItem(SECTIONS_KEY);
    if (raw) return { ...base, ...(JSON.parse(raw) as Partial<Record<SectionKey, boolean>>) };
  } catch {
    /* fall through to defaults */
  }
  return base;
}

export function CasesPage() {
  const getToken = useApiToken();
  const navigate = useNavigate();
  const me = useMe();
  const meOid = me?.oid ?? null;
  const canManage = me?.canManage ?? false;
  const [reloadNonce, setReloadNonce] = useState(0);

  const [filters, setFilters] = useState<Filters>(loadFilters);
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>(loadSections);
  // Debounced copy of search so typing doesn't fetch per-keystroke.
  const [searchDebounced, setSearchDebounced] = useState(filters.search);

  const [modules, setModules] = useState<QcModule[]>([]);
  const [cases, setCases] = useState<QcCase[]>([]);
  const [people, setPeople] = useState<Record<string, string>>({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // ?report=1 deep link (the WISE shell's "Report an issue" chrome button, or
  // any module linking here) lands with the new-case intake already open.
  // Optional companions, so each app's button pre-fills ITS module:
  //   &module=<wise-iam module id or QC slug>   e.g. module=wms-punch
  //   &env=dev|test|prod                         the environment filed from
  //   &source=<label>                            recorded on the incident
  const [deepLink] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const envRaw = (p.get('env') ?? p.get('environment') ?? '').toLowerCase();
      return {
        report: p.get('report') === '1',
        module: p.get('module')?.trim().toLowerCase() || null,
        environment: (QC_ENVIRONMENTS as readonly string[]).includes(envRaw) ? (envRaw as QcEnvironment) : null,
        source: p.get('source')?.trim().slice(0, 32) || null,
      };
    } catch {
      return { report: false, module: null, environment: null, source: null };
    }
  });
  const [showNewCase, setShowNewCase] = useState(deepLink.report);

  // The docked case (?case=171) — same pane as the dashboard. Below the dock
  // breakpoint a row click navigates to the full page as before.
  const canDock = useCanDock();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = Number(searchParams.get('case')) || null;
  function select(id: number | null) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('case', String(id));
      else next.delete('case');
      return next;
    });
  }
  function openCase(id: number) {
    if (canDock) select(id);
    else navigate(`/cases/${id}`);
  }
  const docked = canDock && !!selectedId;

  // Once the deep-linked intake closes, drop ?report=1 from the address so a
  // refresh or a bookmark does not reopen the form.
  function closeNewCase() {
    setShowNewCase(false);
    if (deepLink.report) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('report');
          next.delete('module');
          next.delete('env');
          next.delete('environment');
          next.delete('source');
          return next;
        },
        { replace: true },
      );
    }
  }

  // Persist filter state per user (localStorage is per browser profile).
  useEffect(() => {
    localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
  }, [filters]);

  // Persist which stage sections are expanded.
  useEffect(() => {
    localStorage.setItem(SECTIONS_KEY, JSON.stringify(openSections));
  }, [openSections]);

  function toggleSection(key: SectionKey) {
    setOpenSections((s) => ({ ...s, [key]: !s[key] }));
  }

  useEffect(() => {
    const handle = window.setTimeout(() => setSearchDebounced(filters.search), 300);
    return () => window.clearTimeout(handle);
  }, [filters.search]);

  // Modules for the filter chip + new-case modal.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const { modules: list } = await api.listModules(token);
        if (!cancelled) setModules(list);
      } catch (err) {
        console.error('[cases] failed to load modules:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  // The filtered list. One fetch of the whole filtered set (capped at 500)
  // so the stage sections below can group + count accurately.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const token = await getToken();
        const res = await api.listCases(token, {
          search: searchDebounced.trim() || undefined,
          moduleId: typeof filters.moduleId === 'number' ? filters.moduleId : undefined,
          type: filters.type,
          priority: filters.priority || undefined,
          filedByMe: filters.filedByMe || undefined,
          pageSize: 500,
        });
        if (cancelled) return;
        setCases(res.cases);
        setPeople(res.people);
        setTotal(res.total);
        setError(null);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load cases');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, searchDebounced, filters.moduleId, filters.type, filters.priority, filters.filedByMe, reloadNonce]);

  function patchFilters(patch: Partial<Filters>) {
    setFilters((f) => ({ ...f, ...patch }));
  }

  // Inline status change from a row (qc.manage only). Optimistic; reverts on
  // error by reloading.
  async function changeStatus(caseId: number, status: QcCaseStatus) {
    setCases((prev) => prev.map((c) => (c.id === caseId ? { ...c, status } : c)));
    try {
      const token = await getToken();
      await api.updateCase(token, caseId, { status });
    } catch {
      setReloadNonce((n) => n + 1); // revert optimistic change by refetching
    }
  }

  // Group the loaded cases into workflow stages. Within "Needs attention",
  // cases whose ball is in the dev court sort first (then the server's
  // updated_at-desc inbox order, preserved by the stable sort).
  const grouped = useMemo(() => {
    const map: Record<SectionKey, QcCase[]> = {
      needs: [],
      accepted: [],
      in_progress: [],
      fixed: [],
      closed: [],
    };
    for (const c of cases) {
      const sec = SECTIONS.find((s) => s.statuses.includes(c.status));
      if (sec) map[sec.key].push(c);
    }
    map.needs.sort((a, b) => {
      const aBall = conversationCourt(a) === 'awaiting_dev' ? 0 : 1;
      const bBall = conversationCourt(b) === 'awaiting_dev' ? 0 : 1;
      return aBall - bBall;
    });
    return map;
  }, [cases]);

  const renderRow = (c: QcCase) => {
    const unread = isUnread(c.id, c.updatedAt);
    // The snippet line: who spoke last, and what they said. Attachment-only
    // notes have an empty body — show the paperclip instead.
    const snippetAuthor = c.lastNoteBy
      ? actorLabel(c.lastNoteBy, meOid, people, c.lastNoteVia, { short: true, selfName: me?.displayName })
      : null;
    const snippetText = c.lastNoteSnippet?.trim() || (c.noteCount > 0 ? '📎 attachment' : null);
    return (
      <div
        key={c.id}
        onClick={() => openCase(c.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') openCase(c.id);
        }}
        role="link"
        tabIndex={0}
        aria-current={docked && c.id === selectedId ? 'true' : undefined}
        className="qc-case-row"
        style={{
          padding: '12px 20px 12px 14px',
          borderBottom: '1px solid var(--brand-border, #D6E4EE)',
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          gap: 12,
          alignItems: 'start',
          cursor: 'pointer',
          background: unread ? 'var(--brand-card-bg, white)' : 'transparent',
        }}
      >
        {/* Unread dot + type icon */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 3 }}>
          <span
            aria-label={unread ? 'Unread activity' : undefined}
            title={unread ? 'Activity since you last opened this case' : undefined}
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: unread ? 'var(--brand-interactive, #2F6CB5)' : 'transparent',
              flexShrink: 0,
            }}
          />
          <TypeIcon type={c.type} />
        </div>

        <div style={{ minWidth: 0 }}>
          {/* Title line */}
          <div
            style={{
              fontSize: 14,
              fontWeight: unread ? 700 : 500,
              marginBottom: 3,
              color: 'var(--brand-text, #001C3E)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {c.title}
          </div>
          {/* Meta line */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 11,
              color: 'var(--brand-text-muted, #8BA4B8)',
              flexWrap: 'wrap',
              marginBottom: 4,
            }}
          >
            <span style={{ fontFamily: MONO, whiteSpace: 'nowrap' }}>{c.caseNumber}</span>
            <ModuleBadge slug={c.moduleSlug} name={c.moduleName} />
            {c.submoduleName && <span>/ {c.submoduleName}</span>}
            <span title={absoluteTime(c.createdAt)}>
              {personLabel(c.openedBy, meOid, people, { selfName: me?.displayName })} · opened{' '}
              {relativeTime(c.createdAt)}
            </span>
            {c.incidentCount > 1 && <span>· {c.incidentCount} reports</span>}
            {c.priority === 'high' && <PriorityPill priority="high" />}
          </div>
          {/* Snippet line — the last thing said on the case */}
          <div
            style={{
              fontSize: 12.5,
              color: 'var(--brand-text-muted, #5A6B7B)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {snippetAuthor && snippetText ? (
              <>
                <span style={{ fontWeight: 600, color: 'var(--brand-text, #001C3E)' }}>
                  {snippetAuthor}:
                </span>{' '}
                {snippetText}
              </>
            ) : (
              <span style={{ color: '#A4B2BE' }}>No replies yet</span>
            )}
          </div>
        </div>

        {/* Right rail: last activity time, status control, court chip.
            ≤480px it drops to its own full-width row (qc-case-row-side). */}
        <div
          className="qc-case-row-side"
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}
          onClick={(e) => e.stopPropagation()}
        >
          <span
            style={{ fontSize: 11, color: 'var(--brand-text-muted, #8BA4B8)' }}
            title={absoluteTime(c.lastNoteAt ?? c.updatedAt)}
          >
            {relativeTime(c.lastNoteAt ?? c.updatedAt)}
          </span>
          {canManage ? (
            <select
              value={c.status}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => void changeStatus(c.id, e.target.value as QcCaseStatus)}
              title="Change status"
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '3px 6px',
                borderRadius: 6,
                border: '1px solid var(--brand-border, #D6E4EE)',
                background: 'var(--brand-card-bg, white)',
                color: 'var(--brand-text, #001C3E)',
                cursor: 'pointer',
              }}
            >
              {QC_CASE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          ) : (
            <StatusPill status={c.status} />
          )}
          <CourtChip court={conversationCourt(c)} />
        </div>
      </div>
    );
  };

  return (
    <div
      className="qc-workspace"
      style={{
        padding: '24px 0',
        display: 'grid',
        gridTemplateColumns: docked ? 'minmax(0, 1fr) minmax(460px, 46%)' : 'minmax(0, 1fr)',
        gap: 16,
        alignItems: 'start',
      }}
    >
      <div
        style={{
          background: 'var(--brand-card-bg, white)',
          border: '1px solid var(--brand-border, #D6E4EE)',
          borderRadius: 8,
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        {/* Header */}
        <div style={{ padding: '20px 20px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 500, color: 'var(--brand-text, #001C3E)' }}>
              Active cases
            </h2>
            <button type="button" onClick={() => setShowNewCase(true)} style={{ ...primaryButtonStyle, fontSize: 12, padding: '5px 12px' }}>
              + New case
            </button>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--brand-text-muted, #8BA4B8)' }}>
            Bugs and feature requests across all WISE modules
          </p>
        </div>

        {/* Filter bar */}
        <div
          style={{
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            borderTop: '1px solid var(--brand-border, #D6E4EE)',
            borderBottom: '1px solid var(--brand-border, #D6E4EE)',
          }}
        >
          <input
            type="text"
            value={filters.search}
            onChange={(e) => patchFilters({ search: e.target.value })}
            placeholder="Search cases…"
            style={{ ...inputStyle, flex: 1, minWidth: 180, maxWidth: 280, height: 32, fontSize: 13 }}
          />
          <select
            value={filters.moduleId}
            onChange={(e) => patchFilters({ moduleId: e.target.value ? Number(e.target.value) : '' })}
            style={chipSelectStyle}
            aria-label="Filter by module"
          >
            <option value="">Module: All</option>
            {[...new Set(modules.map((m) => m.suite))].map((suite) => (
              <optgroup key={suite} label={suite}>
                {modules
                  .filter((m) => m.suite === suite)
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.displayName}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
          <select
            value={filters.priority}
            onChange={(e) => patchFilters({ priority: (e.target.value || '') as QcCasePriority | '' })}
            style={chipSelectStyle}
            aria-label="Filter by priority"
          >
            <option value="">Priority: All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <button
            type="button"
            onClick={() => patchFilters({ filedByMe: !filters.filedByMe })}
            aria-pressed={filters.filedByMe}
            style={{
              fontSize: 12,
              padding: '5px 10px',
              borderRadius: 6,
              cursor: 'pointer',
              border: filters.filedByMe
                ? '1px solid var(--brand-primary, #001C3E)'
                : '1px solid var(--brand-border, #D6E4EE)',
              background: filters.filedByMe ? 'var(--brand-primary, #001C3E)' : 'transparent',
              color: filters.filedByMe
                ? 'var(--brand-text-on-primary, white)'
                : 'var(--brand-text-muted, #8BA4B8)',
            }}
          >
            Filed by me
          </button>
        </div>

        {/* The two lanes. No "All" tab on purpose — a mixed list invites
            treating a feature request like a bug fix. */}
        <div
          style={{
            display: 'flex',
            gap: 2,
            padding: '0 8px',
            borderBottom: '1px solid var(--brand-border, #D6E4EE)',
          }}
        >
          {(
            [
              { v: 'bug', label: '🐛 Bugs' },
              { v: 'feature_request', label: '✨ Feature requests' },
            ] as { v: QcCaseType; label: string }[]
          ).map((t) => {
            const active = filters.type === t.v;
            return (
              <button
                key={t.v}
                type="button"
                onClick={() => patchFilters({ type: t.v })}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '9px 14px',
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  color: active ? 'var(--brand-primary, #001C3E)' : 'var(--brand-text-muted, #8BA4B8)',
                  borderBottom: `2px solid ${active ? 'var(--brand-interactive, #2F6CB5)' : 'transparent'}`,
                  marginBottom: -1,
                  cursor: 'pointer',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Rows */}
        {error && (
          <div style={{ padding: '16px 20px', fontSize: 13, color: '#900' }}>{error}</div>
        )}
        {!error && loading && cases.length === 0 && (
          <div style={{ padding: '24px 20px', fontSize: 13, color: 'var(--brand-text-muted, #8BA4B8)' }}>
            Loading cases…
          </div>
        )}
        {!error && !loading && cases.length === 0 && (
          <div style={{ padding: '24px 20px', fontSize: 13, color: 'var(--brand-text-muted, #8BA4B8)' }}>
            No cases match these filters.
          </div>
        )}
        {!error &&
          cases.length > 0 &&
          SECTIONS.map((section) => {
            const rows = grouped[section.key];
            if (rows.length === 0) return null;
            const open = openSections[section.key];
            const unreadCount = rows.filter((c) => isUnread(c.id, c.updatedAt)).length;
            return (
              <div key={section.key}>
                <button
                  type="button"
                  onClick={() => toggleSection(section.key)}
                  aria-expanded={open}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '9px 20px',
                    background: 'var(--brand-bg, #F4F8FB)',
                    border: 'none',
                    borderBottom: '1px solid var(--brand-border, #D6E4EE)',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: '0.3px',
                    textTransform: 'uppercase',
                    color: 'var(--brand-text-muted, #5A6B7B)',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ fontSize: 10, width: 10, display: 'inline-block' }}>
                    {open ? '▼' : '▶'}
                  </span>
                  {section.label}
                  <span style={{ fontWeight: 500, color: 'var(--brand-text-muted, #8BA4B8)' }}>
                    ({rows.length})
                  </span>
                  {unreadCount > 0 && (
                    <span
                      title={`${unreadCount} with unread activity`}
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: 'var(--brand-text-on-primary, white)',
                        background: 'var(--brand-interactive, #2F6CB5)',
                        borderRadius: 10,
                        padding: '1px 7px',
                      }}
                    >
                      {unreadCount} new
                    </span>
                  )}
                </button>
                {open && rows.map(renderRow)}
              </div>
            );
          })}

        {/* Footer */}
        <div
          style={{
            padding: '10px 20px',
            background: 'var(--brand-bg, #F4F8FB)',
            fontSize: 11,
            color: 'var(--brand-text-muted, #8BA4B8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span>
            {total === 0 ? 'No active cases' : `${total} active case${total === 1 ? '' : 's'}`}
          </span>
          <span>Archive auto-runs 30d after close</span>
        </div>
      </div>

      {docked && selectedId && (
        <CaseView
          key={selectedId}
          caseId={selectedId}
          variant="pane"
          seed={cases.find((c) => c.id === selectedId) ?? null}
          onClose={() => select(null)}
          onChanged={(fresh) => setCases((prev) => prev.map((c) => (c.id === fresh.id ? { ...c, ...fresh } : c)))}
        />
      )}

      {showNewCase && (
        <NewCaseModal
          modules={modules}
          onClose={closeNewCase}
          onCreated={(caseId) => {
            closeNewCase();
            setReloadNonce((n) => n + 1);
            openCase(caseId);
          }}
          onAttached={(caseId) => {
            closeNewCase();
            setReloadNonce((n) => n + 1);
            openCase(caseId);
          }}
          initialModuleKey={deepLink.report ? deepLink.module : null}
          initialEnvironment={deepLink.report ? deepLink.environment : null}
          source={deepLink.report ? (deepLink.source ?? 'deep-link') : null}
        />
      )}
    </div>
  );
}
